// Pre-built veterinary anaesthesia training cases.
// Each scenario fills the patient intake form and (optionally) a suggested plan.
export const CASE_SCENARIOS = [
  {
    id: "boxer_dental_murmur",
    title: "Geriatric Boxer · Dental",
    blurb: "8-yr MN Boxer, grade III/VI left apical systolic murmur. For dental scale & polish.",
    difficulty: "Advanced",
    patient: {
      species: "dog",
      name: "Rocky",
      breed: "Boxer",
      sex: "male_neutered",
      ageYears: "8",
      ageMonths: "0",
      weightKg: "32",
      asa: "III",
      bcs: "6",
      procedure: "Dental scale, polish, extractions PRN",
      notes: "Grade III/VI apical systolic murmur. Echo: mild MR. On pimobendan 0.25 mg/kg BID. Recent bloods unremarkable.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "persian_castration",
    title: "Young Persian · Castration",
    blurb: "6-mo male Persian for routine castration. Brachycephalic, otherwise healthy.",
    difficulty: "Beginner",
    patient: {
      species: "cat",
      name: "Milo",
      breed: "Persian",
      sex: "male_entire",
      ageYears: "0",
      ageMonths: "6",
      weightKg: "3.4",
      asa: "I",
      bcs: "5",
      procedure: "Elective castration",
      notes: "Brachycephalic — careful with airway. No prior anaesthesia. Fasted appropriately.",
    },
    suggestedPlan: {
      premeds: ["buprenorphine", "dexmedetomidine"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "ckd_cat_mass",
    title: "Geriatric DSH · CKD",
    blurb: "13-yr FS Domestic Shorthair, IRIS stage 2 CKD. For mammary mass removal.",
    difficulty: "Advanced",
    patient: {
      species: "cat",
      name: "Whiskey",
      breed: "Domestic Shorthair",
      sex: "female_spayed",
      ageYears: "13",
      ageMonths: "0",
      weightKg: "3.1",
      asa: "III",
      bcs: "4",
      procedure: "Mammary mass excision",
      notes: "Creatinine 2.4 mg/dL, SDMA 21. USG 1.018. On renal diet. Avoid NSAIDs and α2-agonists.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "etomidate",
      maintenance: "sevoflurane",
    },
  },
  {
    id: "lab_ovh",
    title: "Healthy Lab · Spay",
    blurb: "2-yr female Labrador for routine ovariohysterectomy.",
    difficulty: "Beginner",
    patient: {
      species: "dog",
      name: "Bella",
      breed: "Labrador Retriever",
      sex: "female_entire",
      ageYears: "2",
      ageMonths: "0",
      weightKg: "26.5",
      asa: "I",
      bcs: "5",
      procedure: "Ovariohysterectomy",
      notes: "Healthy, vaccinated, no medications. Standard elective.",
    },
    suggestedPlan: {
      premeds: ["methadone", "dexmedetomidine"],
      induction: "propofol",
      maintenance: "isoflurane",
    },
  },
  {
    id: "frenchie_boas",
    title: "Brachycephalic · BOAS",
    blurb: "4-yr ME French Bulldog for soft palate resection. High airway risk.",
    difficulty: "Advanced",
    patient: {
      species: "dog",
      name: "Pierre",
      breed: "French Bulldog",
      sex: "male_entire",
      ageYears: "4",
      ageMonths: "0",
      weightKg: "12.8",
      asa: "II",
      bcs: "7",
      procedure: "Soft palate resection (BOAS)",
      notes: "Stertor at rest. Pre-oxygenate 5 min. Have rapid intubation kit ready. High regurgitation risk.",
    },
    suggestedPlan: {
      premeds: ["methadone"],
      induction: "propofol",
      maintenance: "isoflurane",
    },
  },
  {
    id: "geriatric_golden_tplo",
    title: "Geriatric Golden · TPLO",
    blurb: "10-yr MN Golden Retriever for unilateral TPLO. Osteoarthritis.",
    difficulty: "Intermediate",
    patient: {
      species: "dog",
      name: "Buddy",
      breed: "Golden Retriever",
      sex: "male_neutered",
      ageYears: "10",
      ageMonths: "0",
      weightKg: "34",
      asa: "II",
      bcs: "6",
      procedure: "Right TPLO",
      notes: "Long-standing OA, NSAID-managed. Long procedure — plan locoregional block.",
    },
    suggestedPlan: {
      premeds: ["methadone", "acepromazine"],
      induction: "propofol",
      maintenance: "isoflurane",
    },
  },

  // ---------- Breed-specific vulnerability cases ----------
  {
    id: "greyhound_dental",
    title: "Retired Greyhound · Dental",
    blurb: "5-yr MN Greyhound for dental scale & extractions. Sighthound drug sensitivity.",
    difficulty: "Intermediate",
    patient: {
      species: "dog",
      name: "Dash",
      breed: "Greyhound",
      sex: "male_neutered",
      ageYears: "5",
      ageMonths: "0",
      weightKg: "31",
      asa: "II",
      bcs: "4",
      procedure: "Dental scale, polish & extractions",
      notes:
        "Sighthound vulnerability: very low body fat → rapid HYPOTHERMIA and PROLONGED RECOVERY from slow hepatic clearance (avoid thiopental). Naturally high PCV, low-normal protein. Resting sinus BRADYCARDIA is normal for the breed. Plan: active warming from induction, alfaxalone, opioid-sparing, monitor temperature closely.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "doberman_gastropexy",
    title: "Doberman · Prophylactic gastropexy",
    blurb: "6-yr ME Doberman for elective gastropexy. von Willebrand & DCM risk.",
    difficulty: "Advanced",
    patient: {
      species: "dog",
      name: "Kaiser",
      breed: "Doberman Pinscher",
      sex: "male_entire",
      ageYears: "6",
      ageMonths: "0",
      weightKg: "38",
      asa: "II",
      bcs: "5",
      procedure: "Prophylactic gastropexy",
      notes:
        "Breed vulnerabilities: von WILLEBRAND DISEASE → intra-operative HAEMORRHAGE and HYPOTENSION (vWF borderline on screening). Occult DILATED CARDIOMYOPATHY → ventricular ARRHYTHMIA (VPCs). Pre-op ECG + echo advised. Have lidocaine drawn up, support MAP and minimise blood loss.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "propofol",
      maintenance: "isoflurane",
    },
  },
  {
    id: "cavalier_mvd_dental",
    title: "Cavalier KCS · Dental (heart murmur)",
    blurb: "8-yr FS Cavalier for dental. Stage B2 myxomatous mitral valve disease.",
    difficulty: "Advanced",
    patient: {
      species: "dog",
      name: "Daisy",
      breed: "Cavalier King Charles Spaniel",
      sex: "female_spayed",
      ageYears: "8",
      ageMonths: "0",
      weightKg: "8.6",
      asa: "III",
      bcs: "5",
      procedure: "Dental scale, polish & extractions",
      notes:
        "Breed-defining MYXOMATOUS MITRAL VALVE DISEASE: grade IV/VI left apical systolic murmur, echo stage B2, on pimobendan. Poorly tolerates HYPOTENSION, TACHYCARDIA and fluid overload. Avoid α2-agonists. Conservative fluids, support MAP, keep heart rate moderate and steady.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "collie_mdr1_mass",
    title: "Rough Collie · Mass removal (MDR1)",
    blurb: "7-yr FS Rough Collie for flank mass excision. MDR1 (ABCB1) mutant.",
    difficulty: "Advanced",
    patient: {
      species: "dog",
      name: "Skye",
      breed: "Rough Collie",
      sex: "female_spayed",
      ageYears: "7",
      ageMonths: "0",
      weightKg: "24",
      asa: "II",
      bcs: "5",
      procedure: "Lateral flank mass excision",
      notes:
        "MDR1 / ABCB1 mutant (homozygous): EXAGGERATED, PROLONGED SEDATION from acepromazine, butorphanol and some opioids → risk of an over-deep plane, BRADYCARDIA and slow recovery. Reduce sedative/opioid doses ~25–30%, avoid acepromazine, titrate carefully and watch depth.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "propofol",
      maintenance: "isoflurane",
    },
  },
  {
    id: "mainecoon_hcm_neuter",
    title: "Maine Coon · Castration (HCM)",
    blurb: "2-yr ME Maine Coon for castration. Subclinical hypertrophic cardiomyopathy.",
    difficulty: "Advanced",
    patient: {
      species: "cat",
      name: "Atlas",
      breed: "Maine Coon",
      sex: "male_entire",
      ageYears: "2",
      ageMonths: "0",
      weightKg: "7.2",
      asa: "II",
      bcs: "5",
      procedure: "Elective castration",
      notes:
        "Breed-predisposed HYPERTROPHIC CARDIOMYOPATHY (soft gallop sound; echo mild HCM). FLUID-SENSITIVE → conservative rates to avoid pulmonary oedema. Poorly tolerates TACHYCARDIA and ARRHYTHMIA. Keep heart rate low-normal, support MAP, avoid excitement and ketamine.",
    },
    suggestedPlan: {
      premeds: ["buprenorphine", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "chihuahua_dental",
    title: "Toy Chihuahua · Dental",
    blurb: "4-yr FS Chihuahua, 2.1 kg, for dental. Toy-breed thermoregulation & airway.",
    difficulty: "Intermediate",
    patient: {
      species: "dog",
      name: "Pixie",
      breed: "Chihuahua",
      sex: "female_spayed",
      ageYears: "4",
      ageMonths: "0",
      weightKg: "2.1",
      asa: "II",
      bcs: "4",
      procedure: "Dental scale, polish & extractions",
      notes:
        "Toy breed: high surface-area-to-mass ratio → rapid HYPOTHERMIA; risk of HYPOGLYCAEMIA if fasted too long; possible TRACHEAL COLLAPSE causing vagal BRADYCARDIA. Use a small ETT, active warming from induction, dextrose-supplemented fluids and precise (mg-level) dosing.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "ragdoll_ckd_dental",
    title: "Senior Ragdoll · Dental",
    blurb: "11-yr FS Ragdoll, IRIS stage 2 CKD, for dental scale & extractions.",
    difficulty: "Advanced",
    patient: {
      species: "cat",
      name: "Pearl",
      breed: "Ragdoll",
      sex: "female_neutered",
      ageYears: "11",
      ageMonths: "0",
      weightKg: "4.6",
      asa: "III",
      bcs: "4",
      procedure: "Dental scale, polish, extractions PRN",
      notes: "IRIS stage 2 CKD. Mildly elevated creatinine/SDMA. Maintain renal perfusion — IV fluids, avoid hypotension and NSAIDs.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "staffy_pyo",
    title: "Staffy · Pyometra (emergency)",
    blurb: "6-yr FE Staffordshire Bull Terrier, septic pyometra, for emergency ovariohysterectomy.",
    difficulty: "Advanced",
    patient: {
      species: "dog",
      name: "Roxy",
      breed: "Staffordshire Bull Terrier",
      sex: "female_entire",
      ageYears: "6",
      ageMonths: "0",
      weightKg: "19",
      asa: "IV",
      bcs: "5",
      procedure: "Emergency ovariohysterectomy (pyometra)",
      notes: "Septic, dehydrated, tachycardic. Stabilise with IV fluids before induction. High-risk; balanced analgesia and careful monitoring essential.",
    },
    suggestedPlan: {
      premeds: ["methadone", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "rabbit_dental",
    title: "Rabbit · Dental (exotic)",
    blurb: "4-yr entire male rabbit for incisor/molar burring under GA. Exotic airway considerations.",
    difficulty: "Advanced",
    patient: {
      species: "rabbit",
      name: "Clover",
      breed: "Mini Lop",
      sex: "male_entire",
      ageYears: "4",
      ageMonths: "0",
      weightKg: "1.8",
      asa: "II",
      bcs: "3",
      procedure: "Dental burr — incisors/molars",
      notes: "Rabbits don't fast (no vomiting; risk of gut stasis). Challenging airway — often mask/v-gel maintenance. Keep warm, minimise stress, support gut post-op.",
    },
    suggestedPlan: {
      premeds: ["buprenorphine", "midazolam"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
  {
    id: "beagle_fb_laparotomy",
    title: "Beagle · Foreign-body laparotomy",
    blurb: "3-yr MN Beagle, intestinal foreign body, for exploratory laparotomy. Vomiting, mild dehydration.",
    difficulty: "Intermediate",
    patient: {
      species: "dog",
      name: "Cooper",
      breed: "Beagle",
      sex: "male_neutered",
      ageYears: "3",
      ageMonths: "0",
      weightKg: "12.5",
      asa: "III",
      bcs: "5",
      procedure: "Exploratory laparotomy — intestinal foreign body",
      notes: "Vomiting 2 days, mildly dehydrated. Correct fluid deficit before induction. Anti-emesis and full peri-op analgesia indicated.",
    },
    suggestedPlan: {
      premeds: ["methadone", "dexmedetomidine"],
      induction: "alfaxalone",
      maintenance: "isoflurane",
    },
  },
];
